import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { ReactNode } from 'react';

import { css } from '@sorare/css';

import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, LabelS } from '#src/atoms/typography/index.tsx';
import { CardImg } from '#src/components/card/CardImg.tsx';
import { getHumanReadableSerialNumber } from '#src/lib/cards.ts';
import { format } from '#src/lib/seasons.ts';

import type { WarningRow_anyCard } from './__generated__/index.graphql.ts';

const wrapper = css`
  background: var(--c-surface-secondary);
  border-radius: var(--unit);
`;
const playerInfo = css`
  padding: var(--unit);
`;
const warningClass = css`
  background: var(--c-surface-tertiary);
  border-radius: 0 0 var(--unit) var(--unit);
  padding: var(--unit);
  &.error {
    background: rgba(var(--c-rgb-red-400), 0.1);
  }
`;

type Props = {
  card: WarningRow_anyCard;
  warning: ReactNode;
  warningLevel?: 'error' | 'warning';
};

export const WarningRow = ({
  card,
  warning,
  warningLevel = 'warning',
}: Props) => {
  const { seasonYear, singleCivilYear, pictureUrl, anyPlayer } = card;

  return (
    <Vertical className={wrapper}>
      <Horizontal className={playerInfo} gap={1.5}>
        {!!pictureUrl && (
          <CardImg className="w-5" src={pictureUrl} alt="" width={80} />
        )}
        <div>
          <BodyM>{anyPlayer?.displayName}</BodyM>
          <LabelS color="var(--c-onBase-secondary)">
            {`${format(seasonYear, {
              singleCivilYear,
            })} - ${getHumanReadableSerialNumber(card)}`}
          </LabelS>
        </div>
      </Horizontal>
      <Horizontal className={classNames(warningClass, warningLevel)}>
        {warning}
      </Horizontal>
    </Vertical>
  );
};

WarningRow.fragments = {
  anyCard: gql`
    fragment WarningRow_anyCard on AnyCardInterface {
      slug
      seasonYear
      singleCivilYear
      pictureUrl
      anyPlayer {
        slug
        displayName
      }
      ...getHumanReadableSerialNumber_anyCard
    }
    ${getHumanReadableSerialNumber.fragments.anyCard}
  ` as TypedDocumentNode<WarningRow_anyCard>,
};
